import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Startseite", testid: "nav-link-home" },
  { to: "/leistungen", label: "Leistungen", testid: "nav-link-leistungen" },
  { to: "/referenzen", label: "Referenzen", testid: "nav-link-referenzen" },
  { to: "/kontakt", label: "Kontakt", testid: "nav-link-kontakt" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="header-navigation"
      className="fixed top-4 left-0 right-0 z-50 px-4 sm:px-6"
    >
      <div
        className={`mx-auto flex max-w-6xl items-center justify-between rounded-full border px-5 sm:px-7 py-3 backdrop-blur-xl backdrop-saturate-150 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled ? "bg-white/70 border-white/60 shadow-[0_12px_40px_rgba(10,17,30,0.12)]" : "bg-white/50 border-white/50 shadow-[0_8px_30px_rgba(10,17,30,0.08)]"
        }`}
      >
        <Link to="/" aria-label="HRG Service GmbH Startseite">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Hauptnavigation">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-[#3B62A5]" : "text-slate-600 hover:text-[#0A111E]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-[#3B62A5]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
          <Link
            to="/kontakt"
            data-testid="nav-cta-angebot"
            className="group inline-flex items-center gap-2 rounded-full bg-[#3B62A5] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#2A477A]"
          >
            Offerte anfragen
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-[#0A111E]"
          aria-label={open ? "Menü schliessen" : "Menü öffnen"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden mx-auto mt-2 max-w-6xl overflow-hidden rounded-3xl border border-white/60 bg-white/85 backdrop-blur-2xl shadow-[0_12px_40px_rgba(10,17,30,0.12)]"
            aria-label="Mobile Navigation"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  className={({ isActive }) =>
                    `py-3 text-base font-medium border-b border-slate-100 ${isActive ? "text-[#3B62A5]" : "text-slate-700"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/kontakt"
                data-testid="nav-cta-angebot-mobile"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#3B62A5] px-5 py-3 text-sm font-semibold text-white"
              >
                Offerte anfragen <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
